import React from 'react'
import { cn } from '../../lib/utils'

const MIN_PITCH = -12
const MAX_PITCH = 12

const PitchSheetB = ({
  pitchShift = 0,
  onPitchShiftChange,
  dragHandleProps = {}
}) => {
  const handlePitchStep = (step) => {
    if (typeof onPitchShiftChange !== 'function') return

    const nextValue = Math.max(MIN_PITCH, Math.min(MAX_PITCH, pitchShift + step))
    if (nextValue === pitchShift) return
    onPitchShiftChange(nextValue)
  }

  const handleReset = () => {
    if (typeof onPitchShiftChange !== 'function') return
    if (pitchShift === 0) return
    onPitchShiftChange(0)
  }

  const atLowerBound = pitchShift <= MIN_PITCH
  const atUpperBound = pitchShift >= MAX_PITCH

  return (
    <section
      className="room-elevated room-glow room-content-safe-inline mx-auto w-full max-w-lg rounded-t-3xl border-b-0 pb-4 pt-3 text-[var(--room-text)]"
      aria-label="Pitch control sheet"
    >
      <button
        type="button"
        aria-label="Drag to dismiss utility sheet"
        className="mx-auto mb-3 flex h-6 w-16 touch-none items-center justify-center rounded-full"
        style={{ touchAction: 'none' }}
        {...dragHandleProps}
      >
        <span className="h-1.5 w-12 rounded-full bg-white/20" />
      </button>
      <div className="mb-4">
        <h3 className="text-base font-semibold">Pitch Control</h3>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <button
            type="button"
            onClick={() => handlePitchStep(-1)}
            disabled={atLowerBound}
            className={cn(
              'room-focus-ring h-12 rounded-xl border text-xl font-semibold',
              atLowerBound
                ? 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
                : 'border-white/15 bg-white/5 text-[var(--room-text)] hover:bg-white/10'
            )}
            aria-label="Lower pitch by one semitone"
          >
            -
          </button>

          <div className="rounded-xl border border-white/15 bg-black/25 px-4 py-2 text-center">
            <p className="text-xs uppercase tracking-[0.08em] text-[var(--room-muted)]">Current</p>
            <p className="text-xl font-semibold">{pitchShift > 0 ? `+${pitchShift}` : pitchShift}</p>
          </div>

          <button
            type="button"
            onClick={() => handlePitchStep(1)}
            disabled={atUpperBound}
            className={cn(
              'room-focus-ring h-12 rounded-xl border text-xl font-semibold',
              atUpperBound
                ? 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
                : 'border-white/15 bg-white/5 text-[var(--room-text)] hover:bg-white/10'
            )}
            aria-label="Raise pitch by one semitone"
          >
            +
          </button>
        </div>

        <button
          type="button"
          onClick={handleReset}
          disabled={pitchShift === 0}
          className={cn(
            'room-focus-ring mt-3 h-11 w-full rounded-xl border text-sm font-semibold',
            pitchShift === 0
              ? 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
              : 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.22)] text-[var(--room-text)] hover:bg-[color:rgba(46,168,255,0.28)]'
          )}
          aria-label="Return pitch to original"
        >
          Return to Original
        </button>
      </div>
    </section>
  )
}

export default PitchSheetB
